import { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import "./transaction.css";
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { arabicLocaleText } from '../../../utils/arabic-settings';
import ModalContainer from '../../../components/Modal/ModalContainer';
import WithDrawModal from '../../../components/Modal/WithDrawModal';
import useModal from '../../../hooks/useModal';
import AddPayment from './AddPayment';
import { useFetchData } from '../../../hooks/useFetchData';
import { useAuthContext } from '../../../hooks/useAuthContext';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { maxWidth } from '@mui/system';

function TransactionsPage() {
  const { auth } = useAuthContext();
  const { data, loading, error } = useFetchData("mandoub/contributions");
  const { data: salaryData} = useFetchData("mandoub/salary?id="+auth?.user._id,[auth]);
  const [rows, setRows] = useState([]);
  const { data: levelData } = useFetchData("mandoub/leveldata?id=" + auth.user?._id, [auth]);
  const { data: stats } = useFetchData("mandoub/honeyTransaction");
  const [targetsArray, setTargetsArray] = useState([]);
  const [categorySalesArray, setCategorySalesArray] = useState([]);
  const { modalOpen, close, open } = useModal();
  
  // Add state for tracking whether all targets are met
  const [canWithdraw, setCanWithdraw] = useState(false);

  // Chart options state
  const [chartOptions, setChartOptions] = useState({
    series: [
      { name: 'الراتب', data: [] },
      { name: 'المكافأة', data: [] }
    ],
    chart: {
      height: 350,
      type: 'area',
      zoom: { enabled: false }
    },
    colors: ['#000000', '#FF6347'],
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth' },
    xaxis: {
      type: 'category',
      categories: []
    },
    tooltip: { x: { format: 'MMM yyyy' } }
  });
  useEffect(() => {
    if (salaryData?.success && salaryData?.salary) {
      const transformedRows = salaryData.salary.map((entry, index) => ({
        id: entry._id,
        salary: entry.amount,
        bonus: entry.reward,
        gift: entry.giftName,
        transactiondate: new Date(entry.createdAt).toLocaleDateString('en-GB'),
        method: {
          cardname: entry.method,
          imageUrl: '/img/default.png' // Use a default or conditional image
        },
        total: entry.amount + entry.reward
      }));

      setRows(transformedRows);
    }
  }, [salaryData]);


  // DataGrid columns definition
  const columns = [
    { field: 'id', headerName: 'المعرف', width: 100 },
    { field: 'salary', headerName: 'الراتب', flex: 1 },
    { field: 'bonus', headerName: 'المكافأة المالية', flex: 2 },
    { field: 'gift', headerName: 'المكافأة المادية', flex: 2 },
    { field: 'total', headerName: 'الإجمالي', flex: 1 },
    { field: 'transactiondate', headerName: 'تاريخ المعاملة', flex: 1 },
    {
      field: 'method',
      headerName: 'طريقة الدفع',
      flex: 2, // Plus d'espace pour cette colonne
      renderCell: (params) => (
        <div className='df text-xs font-bold'>
          <div>{params.value.cardname}</div>
        </div>
      )
    }
  ];
  

  // Your existing useEffects for data setting
  useEffect(() => {
    if (levelData?.success && levelData?.level.targets) {
      setTargetsArray(levelData?.level.targets);
    }
  }, [levelData]);

  useEffect(() => {
    if (stats?.success && stats?.data.totalSalesByCategoryForCurrentMonth) {
      setCategorySalesArray(stats?.data.totalSalesByCategoryForCurrentMonth);
    }
  }, [stats]);

  // Chart data update effect
  useEffect(() => {
    if (data) {
      const salaryData = [];
      const bonusData = [];
      const categories = [];
  
      data.salaryStats.recentSalaries.forEach(entry => {
        const { month, salary, bonus } = entry;
        
        salaryData.push(salary);
        bonusData.push(bonus);
        categories.push(month);
      });
  
      setChartOptions(prevOptions => ({
        ...prevOptions,
        series: [
          { name: 'الراتب', data: salaryData },
          { name: 'المكافأة', data: bonusData }
        ],
        xaxis: {
          ...prevOptions.xaxis,
          categories
        }
      }));
    }
  }, [data]);

  // Effect to check all targets
  useEffect(() => {
    const checkAllTargetsReached = () => {
      // Guard clause if we don't have all necessary data
      if (!targetsArray.length || !categorySalesArray.length) {
        setCanWithdraw(false);
        return;
      }

      // Check if all category targets are met
      const allCategoryTargetsMet = targetsArray.every(target => {
        const matchingCategory = categorySalesArray.find(
          sale => sale.category._id === target.category._id
        );
        const achievedQuantity = matchingCategory ? matchingCategory.totalQuantity : 0;
        return achievedQuantity >= target.monthlyTarget;
      });
        setCanWithdraw(allCategoryTargetsMet);
    };

    checkAllTargetsReached();
  }, [targetsArray, categorySalesArray, levelData]);
  const bonusValue = stats?.data?.totalSalesByCategoryForCurrentMonth.reduce((acc,item) =>acc+item.totalQuantity,0);

  return (
    <div className='df-c transactionpage'>
      <ModalContainer>
        {modalOpen&& stats && (
          <WithDrawModal
            modalOpen={modalOpen}
            handleClose={close}
            bonusTarget ={levelData.level.rewardTarget} 
            bonus={bonusValue}
          />
        )}
      </ModalContainer>
      <div className="df ai-stretch sm-fd-col">
        <AddPayment />
        <div className='section-card flex1'>
          <ReactApexChart
            options={chartOptions}
            series={chartOptions.series}
            type="area"
            height={350}
          />
        </div>
      </div>
      <div className="df ai-stretch">
        <div className='section-card wsm' style={{ height: 460, width: "60%", overflowX: "auto" }}>
          معاملاتك الأخيرة
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            components={{
              Toolbar: GridToolbar,
            }}
            hideFooterPagination={true}
            localeText={arabicLocaleText}
            disableColumnResize={false} 
          />
        </div>
        <div className="section-card df-c jc-sb flex-1">
          <h2 className='bolder'>أهداف راتبك التالي:</h2>
          <div className="actions my-8 df-c">
          {targetsArray && targetsArray.map((target, index) => {
            const matchingCategorySale = categorySalesArray.find(
              sale => sale.category._id === target.category._id
            );
            const totalQuantity = matchingCategorySale ? matchingCategorySale.totalQuantity : 0;
            const percentage = (totalQuantity / target.monthlyTarget) * 100;
            const remaining = target.monthlyTarget - totalQuantity;

            return (
              <div key={index} className="CompactCard">
                <div className={`radialBar`}>
                  <CircularProgressbar value={percentage} text={`${Math.round(percentage)}%`} styles={buildStyles({
                         rotation: 0.25,
                         strokeLinecap: 'butt',
                       textSize: '46px',
                     pathTransitionDuration: 0.5,
                      pathColor: `rgba(62, 152, 199, ${percentage / 100})`,
                      textColor: 'white',
                      trailColor: '#d6d6d6',
                  backgroundColor: '#3e98c7',
                      })}/>
                  <span>{remaining > 0 ? remaining : 0} كغ متبقية</span>
                </div>
                <div className="detail">
                  <div className="df">
                    <img src={target.category.icon[0].url} className="w-12 h-12 object-cover" alt="" />
                    {target.category.name}
                  </div>
                  <span>{target.monthlyTarget} كغ</span>
                  <span>{totalQuantity}/{target.monthlyTarget} تم التوصيل</span>
                </div>
              </div>
            );
          })}

          {stats && levelData && levelData.level.rewardTarget && (
            <div className="CompactCard cyan">
              <div className="radialBar">
                {(() => {
                  const totalQuantity = categorySalesArray.reduce((acc, item) => acc + item.totalQuantity, 0);
                  const rewardTarget = levelData.level.rewardTarget;
                  const percentage = (totalQuantity / rewardTarget) * 100;
                  const roundedPercentage = Math.round(percentage);

                  return (
                    <>
                      <CircularProgressbar value={percentage} styles={buildStyles({
                         rotation: 0.25,
                         strokeLinecap: 'butt',
                       textSize: '43px',
                     pathTransitionDuration: 0.5,
                      pathColor: `rgba(62, 152, 199, ${percentage / 100})`,
                      textColor: 'white',
                      trailColor: '#d6d6d6',
                  backgroundColor: '#3e98c7',
                      })} text={`${roundedPercentage}%`} />
                      <span>{`${rewardTarget - totalQuantity} كغ متبقية`}</span>
                    </>
                  );
                })()}
              </div>
              <div className="detail">
                <div className="df">
                  <img src="https://cdn-icons-png.flaticon.com/512/9908/9908288.png" className="w-12 h-12 object-cover" alt="Default" />
                  المكافأة
                </div>
                <span>{`${levelData.level.rewardTarget} كغ`}</span>
                <span>
                  {`${categorySalesArray.reduce((acc, item) => acc + item.totalQuantity, 0)}/${levelData.level.rewardTarget} تم التوصيل`}
                </span>
              </div>
            </div>
          )}
          </div>
          {/* Only show withdrawal button if all targets are met */}
          { stats && (
            <button onClick={open} className="btn bg-2">سحب</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default TransactionsPage;